import React from "react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/home/Hero";
import { ServiceCard } from "../components/home/ServiceCard";
import {
  SERVICES,
  INDUSTRIES,
  STATS,
  KEY_HIGHLIGHTS,
  LIFECYCLE_STEPS,
  AI_FEATURES,
  SAFETY_POINTS,
  CLIENTS,
  CSR_IMAGES,
  CSR_INITIATIVES,
} from "../data/mockData";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Icon from "../components/common/Icon";

const Home = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-grow">
        <Hero />

        {/* Key Highlights bar */}
        <section className="bg-primary text-white py-8" data-testid="key-highlights">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {KEY_HIGHLIGHTS.map((h) => (
                <div key={h.title} className="flex items-center gap-3">
                  <div className="text-gold shrink-0">
                    <Icon name={h.icon} size={26} />
                  </div>
                  <span className="text-sm font-medium leading-tight">{h.title}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Snippet */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-gray-100 -z-10"></div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gold/20 -z-10"></div>
                <img
                  src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?auto=format&fit=crop&w=1200&q=80"
                  alt="Industrial Facility"
                  className="w-full h-[500px] object-cover shadow-2xl filter grayscale hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute bottom-10 left-10 bg-white p-6 shadow-xl max-w-xs border-l-4 border-gold">
                  <p className="font-display font-bold text-4xl text-primary mb-1">15+</p>
                  <p className="text-gray-500 text-sm uppercase tracking-wider">Years of Industrial Excellence</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-0.5 w-12 bg-gold"></span>
                  <span className="text-gold font-bold uppercase tracking-widest text-sm">Who We Are</span>
                </div>
                <h2 className="text-4xl font-display font-bold text-foreground leading-tight">
                  Integrated Industrial Solutions <br />
                  <span className="text-primary">Under One Roof</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Yantraksh Projects Pvt. Ltd. is a premier execution specialist serving the Mining and Steel sectors with integrated civil, mechanical, electrical, maintenance and regulatory solutions.
                </p>
                <p className="text-gray-500 leading-relaxed">
                  From site preparation and clearances to daily plant operations and maintenance, we provide end-to-end project support — reducing downtime, improving efficiency and accelerating execution.
                </p>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  {["Single-Point Ownership", "Predictive O&M", "Regulatory Compliance", "Cost Optimization"].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-gold" />
                      <span className="text-sm font-medium text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-6">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white rounded-none px-8 h-12 uppercase tracking-wider" asChild>
                    <Link to="/about">More About Us</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">Our Expertise</span>
              <h2 className="text-4xl font-display font-bold text-foreground mb-4">Core Service Verticals</h2>
              <div className="h-1 w-24 bg-gold mx-auto"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {SERVICES.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* Lifecycle Asset Management */}
        <section className="py-24 bg-white" data-testid="lifecycle-section">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">End-to-End Stewardship</span>
              <h2 className="text-4xl font-display font-bold text-foreground mb-4">Lifecycle Asset Management</h2>
              <p className="text-gray-500">We manage every stage of the industrial asset lifecycle — from approvals to continuous optimization.</p>
            </div>
            <div className="relative">
              <div className="hidden lg:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-gold to-primary"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-8">
                {LIFECYCLE_STEPS.map((step) => (
                  <div key={step.n} className="relative text-center lg:text-left">
                    <div className="h-16 w-16 mx-auto lg:mx-0 rounded-full bg-primary text-white flex items-center justify-center font-display font-bold text-xl border-4 border-white shadow-lg relative z-10">
                      {step.n}
                    </div>
                    <h4 className="font-display font-bold text-foreground mt-4 mb-1 text-sm uppercase">{step.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* AI & Innovation */}
        <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-testid="ai-section">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <span className="h-0.5 w-12 bg-gold"></span>
                  <span className="text-gold font-bold uppercase tracking-widest text-sm">Innovation</span>
                </div>
                <h2 className="text-4xl font-display font-bold leading-tight">AI-Powered Industrial Optimization</h2>
                <p className="text-gray-300 leading-relaxed">
                  We are developing AI-driven operational models that analyze plant data, optimize resources, and automate predictive maintenance — turning raw industrial data into reliability and cost savings.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {AI_FEATURES.map((f) => (
                    <div key={f.title} className="flex items-center gap-3 bg-white/5 border border-white/10 p-3">
                      <span className="text-gold"><Icon name={f.icon} size={20} /></span>
                      <span className="text-sm">{f.title}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80" alt="AI control room" className="w-full h-64 object-cover shadow-lg" />
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80" alt="Industrial dashboard" className="w-full h-64 object-cover shadow-lg mt-8" />
              </div>
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16">
              <div>
                <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">Sectors</span>
                <h2 className="text-4xl font-display font-bold text-foreground">Industries We Serve</h2>
              </div>
              <Button variant="link" className="text-primary hover:text-gold uppercase tracking-wider text-sm mt-4 md:mt-0 p-0" asChild>
                <Link to="/industries">View All Industries <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {INDUSTRIES.map((ind, idx) => (
                <div key={idx} className="bg-white border border-gray-100 p-8 hover:shadow-xl transition-shadow group cursor-pointer">
                  <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-primary transition-colors text-primary group-hover:text-white">
                    <Icon name={ind.icon} size={24} />
                  </div>
                  <h3 className="text-lg font-display font-bold mb-2 text-foreground">{ind.name}</h3>
                  <div className="w-8 h-0.5 bg-gold group-hover:w-full transition-all duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Safety */}
        <section className="py-24 bg-white" data-testid="safety-section">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">Safety & Reliability</span>
              <h2 className="text-4xl font-display font-bold text-foreground mb-4">Zero-Tolerance Safety Commitment</h2>
              <div className="h-1 w-24 bg-gold mx-auto"></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {SAFETY_POINTS.map((s) => (
                <div key={s.title} className="text-center p-6 border border-gray-100 hover:border-gold transition-colors">
                  <div className="h-14 w-14 mx-auto rounded-full bg-primary/5 flex items-center justify-center text-primary mb-4">
                    <Icon name={s.icon} size={26} />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{s.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-20 bg-primary">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/20">
              {STATS.map((stat, idx) => (
                <div key={idx} className="p-4">
                  <p className="text-4xl md:text-5xl font-display font-bold text-gold mb-2">{stat.value}</p>
                  <p className="text-white/80 text-sm uppercase tracking-widest">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CSR Section */}
        <section className="py-24 bg-gray-50" data-testid="csr-section">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img src={CSR_IMAGES[0]} alt="CSR initiative" className="w-full h-56 object-cover shadow-lg" />
                  <img src={CSR_IMAGES[1]} alt="CSR initiative" className="w-full h-40 object-cover shadow-lg" />
                </div>
                <div className="pt-10">
                  <img src={CSR_IMAGES[2]} alt="CSR initiative" className="w-full h-72 object-cover shadow-lg" />
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-0.5 w-12 bg-gold"></span>
                  <span className="text-gold font-bold uppercase tracking-widest text-sm">Giving Back</span>
                </div>
                <h2 className="text-4xl font-display font-bold text-foreground leading-tight">
                  Corporate Social <span className="text-primary">Responsibility</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Beyond engineering excellence, we are committed to creating lasting positive impact for the communities and environments where we operate. <span className="italic">(Placeholder copy — update with your CSR story.)</span>
                </p>
                <div className="space-y-6 pt-2">
                  {CSR_INITIATIVES.map((item) => (
                    <div key={item.title} className="flex items-start gap-4" data-testid={`csr-item-${item.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                      <div className="bg-gold/15 p-3 rounded-sm text-gold shrink-0">
                        <Icon name={item.icon} size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground font-display">{item.title}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Clients */}
        <section className="py-20 bg-white" data-testid="clients-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">Trusted By</span>
              <h2 className="text-3xl font-display font-bold text-foreground">Clients & Industries Served</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
              {CLIENTS.map((c) => (
                <div key={c} className="h-24 border border-gray-100 bg-gray-50 flex items-center justify-center text-center px-3 text-sm font-display font-bold text-gray-400 uppercase tracking-wide hover:text-primary hover:border-gold transition-colors">
                  {c}
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button className="bg-primary text-white rounded-none px-8 py-6 uppercase tracking-widest" asChild>
                <Link to="/case-studies">View Case Studies <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-slate-900 text-white text-center relative overflow-hidden">
          <div className="container mx-auto px-4 max-w-4xl relative z-10">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Ready for Reliable Industrial Performance?
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Partner with Yantraksh Projects to optimize your asset lifecycle and ensure operational excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gold hover:bg-gold/90 text-slate-900 rounded-none px-10 py-6 font-display uppercase tracking-widest text-sm font-bold" asChild>
                <Link to="/contact">Request Consultation</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 rounded-none px-10 py-6 font-display uppercase tracking-widest text-sm bg-transparent" asChild>
                <Link to="/services">Explore Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
